import React, { useCallback, useEffect, useState } from "react";
import style from "./measurement.module.scss";
import { Button, AccessErrorModal } from "../../../primary";
import { useDispatch, useSelector } from "react-redux";
import {
  setWoundMeasurementData,
  getWoundMeasurements,
  ne1updateAssessmentNewMeasurementAsync,
} from "../../../../app/patientSlice";
import { useParams } from "react-router-dom";
import editIcon from "../../../../static/images/edit-icon-blue.png";
import Loader from "../../../../static/images/spinner.svg";
import { fullMonthFormatDate, hasAnyValue, signedLockedAccess, toLocalISOWithOffset } from "../../../../utils";
import iconDanger from "../../../../static/images/icon-check-danger.png";
import noWoundImage from "../../../../static/images/wound_image_not_process.png";
import { EDIT } from "../../../../constants";
import commonStyle from "../../../../styles/common.module.scss";
import { clearError } from "../../../../app/appSlice";
import infoIcon from "../../../../static/images/info.svg";
import { Confirmation } from "../../../secondary/Confirmation/confirmation";
import { Modal } from "../../../secondary";
import { MeasurementSidebar } from "./measurementSidebar";
import { MeasurementStatus } from "./measurementStatus";
import { MeasurementLoader } from "./measurementLoader";
import { EditMeasurement } from "./editMeasurement";
import constants from "../../../../constants/constants.json";
import Tooltip from "../../../secondary/Tooltip/tooltip";
import warning from "../../../../static/images/icon-check-danger.png";
import WoundTracer from "../../../secondary/WoundTracer/WoundTracer";
import { useReadOnly } from "../../../../hooks/useReadOnly";
import { ReadOnlyBanner } from "../../../secondary/ReadOnlyBanner/ReadOnlyBanner";
import WoundDotPlot from "../../../secondary/WoundTracer/WoundDotPlot";

export const Measurement = ({ hideIt }) => {
  const MAX_PENDING_RETRIES = 3;
  const dispatch = useDispatch();
  const { assessmentId, patientId } = useParams();

  const isReadOnly = useReadOnly();
  const [measurementModal, setMeasurementModal] = useState(false);
  const [annotationView, setAnnotationView] = useState(false);
  const [headToToeView, setHeadToToeView] = useState(false);
  const [measurementForm, setMeasurementForm] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [assessedPicture, setAssessedPicture] = useState(null);
  const [surfaceArea, setSurfaceArea] = useState(null);
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [pendingRetires, setPendingRetires] = useState(0);
  const [pendingTimer, setPendingTimer] = useState(15000);
  const [imageSrc, setImageSrc] = useState(null);
  const [isFallback, setIsFallback] = useState(false);
  const assessmentDetail = useSelector((state) => state.patient.detailAssessment);
  const ne1getWounAssessmentDetailData = useSelector((state) => state.patient.ne1getWounAssessmentDetail);
  const { loading, measurementData } = useSelector((state) => state.patient.newWoundMeasurements) || { loading: false, measurementData: null };
  const appLoading = useSelector((state) => state.app.loading);
  const errorMessage = useSelector((state) => state.app.errorMessage);

  const fetchMeasurements = useCallback(() => {
    const payload = {
      assessmentid: assessmentId,
      patientId,
    };
    dispatch(getWoundMeasurements(payload));
  }, [assessmentId, patientId, dispatch]);

  useEffect(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  useEffect(() => {
    let timer = null;

    if (measurementData?.status === "pending" && !measurementData?.measurementRetry) {
      timer = setTimeout(() => {
        setPendingRetires((prev) => prev + 1);
        fetchMeasurements();
      }, pendingTimer);
    }

    return () => clearTimeout(timer);
  }, [fetchMeasurements, measurementData?.measurementRetry, measurementData?.status, pendingTimer]);

  useEffect(() => {
    if (!measurementData?.intialImageSASURL) {
      setImageSrc(noWoundImage);
      setIsFallback(true);
      return;
    }

    const img = new Image();
    img.src = measurementData.intialImageSASURL;

    img.onload = () => {
      setImageSrc(measurementData.intialImageSASURL);
      setIsFallback(false);
    };

    img.onerror = () => {
      setImageSrc(noWoundImage);
      setIsFallback(true);
    };
  }, [measurementData?.intialImageSASURL]);

  /* auto measurement modal */
  const showMeasurementModal = (type) => {
    setMeasurementModal(true);
    if (type === "Dimensions") {
      setAnnotationView(false);
      setMeasurementForm(true);
      setHeadToToeView(false);
    } else if (type === "PlaceDot") {
      setHeadToToeView(true);
      setAnnotationView(false);
    } else {
      setAnnotationView(true);
      setMeasurementForm(false);
      setHeadToToeView(false);
    }
    setAssessedPicture(undefined);
  };

  // confirmation modal
  const confirmation = () => {
    if (document?.querySelector(".fullscreen") && document?.querySelector(".fullscreen svg > polygon")) {
      setConfirm(true);
    } else {
      assessModalClose();
    }
  };

  const assessModalClose = () => {
    setMeasurementModal(false);
    setAnnotationView(false);
    setHeadToToeView(false);
    setConfirm(false);
    setMeasurementForm(false);
    dispatch(clearError());
  };

  const confirmationClose = () => {
    setConfirm(false);
  };

  useEffect(() => {
    setAssessedPicture(null);
    setSurfaceArea("");
    setLength("");
    setWidth("");
    dispatch(setWoundMeasurementData(null));
    dispatch(clearError());
  }, [dispatch, patientId, assessmentId]);

  useEffect(() => {
    if (measurementData?.assessedImageSASURL) {
      setAssessedPicture(`data:image/jpeg;base64,${measurementData?.assessedImageSASURL}`);
      setAnnotationView(false);
      setHeadToToeView(false);
    } else {
      setAssessedPicture(null);
    }
  }, [measurementData]);

  const createTracingPayload = () => {
    return {
      patient_id: patientId,
      assessment_id: assessmentId,
      wound_type: assessmentDetail?.isWoundWithoutPhoto === 1 ? "new_wound" : "existing_wound",
      blob_info: null,
      blob_image: measurementData?.intialImageSASURL,
      skip_marker: false,
      depth_in_cm: null,
      user_selected_segment: null,
      base64_image: null,
      detections: null,
      camera_meta_data: null,
      device_details: null,
      woundModifiedDate: toLocalISOWithOffset(new Date()),
    };
  };

  const handleTrace = (common, dots) => {
    setHeadToToeView(false);
    const basePayload = createTracingPayload();
    const tracingPayload = {
      ...basePayload,
      idempotency_key: crypto.randomUUID(),
      common: {
        arrow_to_head: common?.arrow_to_head,
        arrow_to_toe: common?.arrow_to_toe,
        total_detections: {
          wound: 0,
          marker: 0,
        },
        segment: "single_segment",
        image_type: "primary",
        capture_status: "manual",
        user_selected_trace: false,
      },
      wound_trace: dots,
      u_net: true,
    };

    dispatch(ne1updateAssessmentNewMeasurementAsync(tracingPayload));
    setPendingTimer(5000);
    setMeasurementModal(false);
  };

  const evalWound = (annotation) => {
    setAnnotationView(false);
    const { regions, pixelSize } = annotation.images[0];
    const pixelPoints = regions?.map((data) =>
      data?.points?.map(([x, y]) => ({
        x: x * pixelSize?.w,
        y: y * pixelSize?.h,
      }))
    );
    const basePayload = createTracingPayload();
    const newWoundPayload = {
      ...basePayload,
      common: {
        image_type: "primary",
        segment: pixelPoints?.length > 1 ? "multi_segment" : "single_segment",
        capture_status: "manual",
        user_selected_trace: true,
        arrow_to_toe: null,
        arrow_to_head: null,
        total_detections: {
          wound: 0,
          marker: 0,
        },
      },
      wound_trace: pixelPoints,
    };
    dispatch(ne1updateAssessmentNewMeasurementAsync(newWoundPayload));
    setPendingTimer(5000);
    setMeasurementModal(false);
  };

  /* State to check editable forms */
  // Edit state
  const confirmationCloseOver = () => {
    setConfirmationModal(false);
    setClickCancel(false);
    setEditBtn(false);
  };
  const yesConfirmationModalClick = () => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
    setEditBtn(true);
  };
  useEffect(() => {
    if (editable) {
      if (formChangesConfirm && clickCancel) {
        setConfirmationModal(true);
      } else if (clickCancel && formChangesConfirm === false) {
        setClickCancel(false);
        setConfirmationModal(false);
        setEditable(false);
        setEditBtn(true);
      }
    }
  }, [formChangesConfirm, clickCancel, editable]);

  const [editBtn, setEditBtn] = useState(true);
  const handleEdit = (edit, cancel) => {
    if (edit === "editable") {
      setEditable(true);
      setEditBtn(false);
    } else if (cancel === "cancel") {
      setClickCancel(true);
    } else {
      setEditable(false);
      setEditBtn(true);
    }
  };

  // <----Measurement Gif Loader when tracing without form populate----->
  const [retrace, setRetrace] = useState(false);
  useEffect(() => {
    if (retrace === true && measurementData !== null) {
      // setSaveClicked(true);
      setMeasurementForm(false);
      setMeasurementModal(false);
      if (editable) {
        setEditable(false);
        setEditBtn(true);
      }
    }
  }, [retrace, measurementData]);
  // <----Measurement Gif Loader----->

  const measurmentObj = {
    surfaceArea: measurementData?.surfaceArea,
    length: measurementData?.length,
    width: measurementData?.width,
    depth: measurementData?.depth,
    volume: measurementData?.volume,
    tunneling: measurementData?.tunneling,
    undermining: measurementData?.undermining,
  };

  return (
    <>
      {loading ? (
        <div className={`${"loader"} col-12`}>
          <img src={Loader} alt="loading" />
        </div>
      ) : (
        <>
          <div>
            {confirmationModal && (
              <Confirmation onClose={() => confirmationCloseOver()} title={" "}>
                <div className="text-center pr-5 pl-5">
                  <p>
                    <img src={iconDanger} alt="warnning-icon" />
                  </p>
                  <p className="pb-3">All progress in this section will be lost. Are you sure want to cancel?</p>
                  <div className="mb-2">
                    <Button
                      value="Yes"
                      classes={{ className: `btn mr-2 pb-2 pt-2 mr-3 ${style.yes_btn}` }}
                      onClick={() => yesConfirmationModalClick()}
                    />
                    <Button
                      id="closeId"
                      value="No"
                      classes={{ className: `btn ml-2 pb-2 pt-2 ${style.no_btn}` }}
                      onClick={() => confirmationCloseOver()}
                    />
                  </div>
                </div>
              </Confirmation>
            )}
            <AccessErrorModal />

            {`${assessmentDetail?.id}` === assessmentId && (
              <div className="d-flex row no-gutters">
                <MeasurementSidebar hideIt={hideIt} showMeasurementModal={showMeasurementModal} setRetrace={setRetrace} isReadOnly={isReadOnly} />

                {assessmentDetail?.isWoundWithoutPhoto !== 1 && ne1getWounAssessmentDetailData?.isWoundPhoto === 0 ? (
                  <div className="col w-100 pl-4 pr-4 text-center">
                    <div className={style.btns_top_space}>
                      <div className={`mb-3 d-flex justify-content-center ${style.details_container}`}>
                        <img className={style.warning_icon} src={warning} alt="warnning-icon" />
                        {constants.assessment.missingWoundPhoto}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="col-8 pt-4 pb-4">
                      {isReadOnly && (
                        <div className="ml-4">
                          <ReadOnlyBanner />
                        </div>
                      )}
                      {signedLockedAccess(assessmentDetail?.assessmentStage) &&
                        hideIt &&
                        assessmentDetail?.isWoundWithoutPhoto !== 1 &&
                        measurementData?.status !== "success" && (
                          <MeasurementStatus
                            showMeasurementModal={showMeasurementModal}
                            pendingRetires={pendingRetires}
                            maxPendingRetries={MAX_PENDING_RETRIES}
                            isReadOnly={isReadOnly}
                          />
                        )}
                      {measurementData?.status !== "pending" && (
                        <div className="w-100 pl-4 pr-4">
                          <div className="d-flex justify-content-between w-100 mb-4">
                            <div className={style.assess_heading}>
                              {measurementData?.measurementDate
                                ? fullMonthFormatDate(measurementData?.measurementDate)
                                : fullMonthFormatDate(assessmentDetail?.identifiedDate)}
                            </div>
                            <div>
                              {signedLockedAccess(assessmentDetail?.assessmentStage) &&
                                hideIt &&
                                editBtn &&
                                (measurementData?.status === "success" || (["error", "no_measurements", "do_manual_trace"].includes(measurementData?.status) && hasAnyValue(measurmentObj) && measurementData?.isStickerAvailable === 0)) && (
                                  <button className={style.edit_btn} onClick={() => handleEdit("editable")} disabled={isReadOnly}>
                                    <img src={editIcon} alt="edit icon" /> {EDIT}
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* editable form */}
                          {!editable && (
                            <div className={style.details_container}>
                              <div className={style.measure_surface}>
                                Surface Area:
                                <span className={`${style.cm_font} pl-1`}>
                                  {measurementData?.surfaceArea && measurementData?.surfaceArea !== "Unknown" ? (
                                    <span className="d-inline-flex">
                                      {`${measurementData?.surfaceArea} cm`}
                                      {assessmentDetail?.isWoundWithoutPhoto === 0 && (
                                        <sup>
                                          <b>2</b>
                                        </sup>
                                      )}
                                      {assessmentDetail?.isWoundWithoutPhoto === 1 && (
                                        <Tooltip
                                          placement="right"
                                          className={style.tooltip}
                                          content={
                                            <>
                                              Surface area is auto-calculated using length <span style={{ fontWeight: "bold" }}>x</span> width formula
                                            </>
                                          }
                                        >
                                          <img src={infoIcon} alt="info" />
                                        </Tooltip>
                                      )}
                                    </span>
                                  ) : (
                                    <span>{measurementData?.surfaceArea}</span>
                                  )}
                                </span>
                              </div>

                              {[
                                { label: "Length", value: measurementData?.length, styleClass: style.measure_length },
                                { label: "Width", value: measurementData?.width, styleClass: style.measure_width },
                                { label: "Depth", value: measurementData?.depth, styleClass: style.measure_items },
                                { label: "Undermining", value: measurementData?.undermining, styleClass: style.measure_items },
                                { label: "Tunneling", value: measurementData?.tunneling, styleClass: style.measure_items },
                              ].map(({ label, value, styleClass }) => (
                                <div key={label} className={styleClass}>
                                  {label}: <span className={style.cm_font}>{value && `${value} cm`}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {editable && (
                            <EditMeasurement
                              setFormChangesConfirm={setFormChangesConfirm}
                              handleEdit={handleEdit}
                              formChanges={true}
                              showCancel={true}
                              buttonClass={style.save_custom}
                              setMeasurementForm={setMeasurementForm}
                              setMeasurementModal={setMeasurementModal}
                              editable={editable}
                              setEditable={setEditable}
                              setEditBtn={setEditBtn}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* measurement modal */}
            {measurementModal && (
              <Modal
                modalOpen={true}
                modalClose={() => confirmation()}
                heading={measurementForm ? "Dimensions" : "Measurements"}
                className={"modal_box"}
                modalSize={style.custom_modal_size}
                confirmation={confirm}
                overlay={" "}
                overlay_class={confirm ? style.overlay_class : ""}
              >
                <div className={style.parent}>
                  {!appLoading && annotationView && (
                    <div className={`col-12 pl-4 pr-4 mb-4 sub_next_clear ${style.img_alert} text-center`}>
                      Manually trace the wound edges to calculate the dimensions
                    </div>
                  )}
                  <div id="wound" className={`row d-flex ${style.annotate_container}`}>
                    {!appLoading && annotationView && (
                      <WoundTracer
                        imageSrc={imageSrc}
                        isFallback={isFallback}
                        onExit={(annotation) => {
                          if (!isFallback) {
                            evalWound(annotation);
                          }
                        }}
                        width={420}
                        height={574}
                      />
                    )}

                    {!appLoading && headToToeView && (
                      <WoundDotPlot
                        imageSrc={imageSrc}
                        isFallback={isFallback}
                        onExit={({ common, dots }) => handleTrace(common, dots)}
                        width={420}
                        height={574}
                      />
                    )}

                    {/* added for measurement git animation loader */}
                    {appLoading && <MeasurementLoader />}

                    {/* added for measurement git animation loader */}
                    {!appLoading && !annotationView && !headToToeView && !measurementForm && !assessedPicture && errorMessage && (
                      <div className="w-100 pl-4 pr-4 text-center">
                        <p>
                          <img src={iconDanger} alt="Error" />
                        </p>
                        <p className="pb-3"> {errorMessage} </p>

                        <div className="col-sm-12">
                          <div className="text-center pb-4">
                            <Button
                              type={"reset"}
                              value={"Ok"}
                              classes={{ className: `btn mr-4 ${commonStyle.btn_custom} ${commonStyle.btn_small}` }}
                              onClick={() => {
                                assessModalClose();
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {!appLoading && measurementForm && (
                      <div className="w-100 pl-4 pr-4 mt-2">
                        <EditMeasurement
                          setFormChangesConfirm={setFormChangesConfirm}
                          fieldDefaults={{ surfaceArea, length, width }}
                          buttonClass={`btn ${style.next_btn_sapce} ${style.btn_custom}`}
                          setMeasurementForm={setMeasurementForm}
                          setMeasurementModal={setMeasurementModal}
                          editable={editable}
                          setEditable={setEditable}
                          setEditBtn={setEditBtn}
                        />
                      </div>
                    )}
                  </div>
                  {confirm && (
                    <div className={style.confirmation_container}>
                      <div className={`${style.confirmation} d-flex align-items-center`}>
                        <div>All unsaved changes will be lost. Do you want to leave?</div>
                        <div>
                          <Button
                            type="button"
                            value="Yes"
                            classes={{ className: `btn ${style.yes_btn}` }}
                            ariaLabel="yes"
                            onClick={() => assessModalClose()}
                          />
                          <Button
                            type="button"
                            value="No"
                            classes={{ className: `btn ${style.no_btn}` }}
                            ariaLabel="no"
                            onClick={() => confirmationClose()}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Modal>
            )}
          </div>
        </>
      )}
    </>
  );
};
